export interface User {
  id: string
  email?: string
  username?: string
  phone_number?: string
  first_name?: string
  last_name?: string
  full_name?: string
  auth_method?: string
  is_active?: boolean
  /** @deprecated Compatibility mirror only; never use for authorization decisions. */
  is_superuser?: boolean
  is_verified?: boolean
  /** @deprecated Compatibility mirror only; never use for authorization decisions. */
  is_hr_admin?: boolean
  /** @deprecated Compatibility mirror only; never use for authorization decisions. */
  is_coach?: boolean
  /** @deprecated Compatibility mirror only; never use for authorization decisions. */
  is_coach_manager?: boolean
  seniority?: string | null
  timezone?: string | null
  last_login?: string | null
  dob?: string | null
  address?: string | null
  gender?: string | null
  organization_id?: number | null
  organization_name?: string | null
  /** Account-level FTUE onboarding-sequence state (PEL-515). */
  ftue_state?: {
    version: number
    completedSteps: string[]
    completedAt: string | null
    loginCount: number
  } | null
  severance_active?: boolean
  severance_start_date?: string | null
  created_at: string
  updated_at: string
}

/** Capitalize each word in a string */
function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Derive a display name from a User object */
export function getUserDisplayName(user: User | null | undefined): string {
  if (!user) return 'User'
  const raw =
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.first_name ||
    user.username ||
    user.email?.split('@')[0] ||
    'User'
  return titleCase(raw)
}

export interface TokenResponse {
  access_token: string
  token_type: string
}

export interface AuthResponse extends TokenResponse {
  user: User
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface OTPRequestData {
  identifier: string // Email or phone number
}

export interface OTPVerifyData {
  identifier: string // Email or phone number
  code: string
}

export interface OTPResponse {
  message: string
  identifier: string
  channel: 'email' | 'sms'
}

export interface RegisterCredentials {
  email: string
  password: string
  name: string
  phone_number?: string | undefined
}

// Message metadata for structured data (replaces [system:...] markers in content)
export interface MessageMatchData {
  score: number
  breakdown: MatchScoreBreakdown
  matchedSkills: string[]
  missingSkills: string[]
  recommendations: string[]
  explanation?: string
  jobTitle?: string
  company?: string
  companyLogoUrl?: string
}

export interface MessageJobContext {
  title?: string
  company?: string
  snippet?: string // First ~100 chars for collapsed preview
  fullText?: string // Full job description text for expanded view
  isUrl?: boolean
}

export interface QuickAction {
  label: string
  action: 'navigate' | 'message' | 'support_request'
  target: string // route path for navigate, message content for message
}

export interface SessionState {
  route: 'career_onboarding' | 'onboarding' | 'job_preferences' | 'opportunities'
  career_onboarding_complete: boolean
  career_onboarding_step: 1 | 2
  onboarding_step: 'resume' | null
  has_resume: boolean
  resume_score: number | null
  has_preferences: boolean
  has_skill_profile: boolean
  has_job_matches: boolean
  emotional_check_in_needed: boolean
  days_since_last_login: number | null
  first_name: string | null
  learning_goal: string | null
  /** Org-level suppression of the business-start/retirement tracks (PEL-909). */
  career_transition_hidden: boolean
}

export interface JobPreferences {
  id: number
  user_id: number
  job_title: string | null
  location: string | null
  locations?: string[] | null
  is_remote: boolean | null
  compensation_min: number | null
  compensation_max: number | null
  compensation_currency: string | null
  employment_type: 'full_time' | 'part_time' | 'contract' | null
  work_culture: 'remote' | 'hybrid' | 'in_person' | null
  created_at: string
  updated_at: string
}

export interface JobPreferencesCreate {
  job_title?: string | null
  location?: string | null
  locations?: string[] | null
  is_remote?: boolean | null
  compensation_min?: number | null
  compensation_max?: number | null
  compensation_currency?: string
  employment_type?: 'full_time' | 'part_time' | 'contract' | null
  work_culture?: 'remote' | 'hybrid' | 'in_person' | null
}

export interface MessageMetadata {
  match_data?: MessageMatchData
  job_context?: MessageJobContext
  revision_data?: RevisionReadyEvent
  quick_actions?: QuickAction[]
  quick_replies?: string[]
  quick_replies_document_id?: number
  quality_score?: QualityScore
  // Resume upload metadata (from backend)
  type?: 'resume_upload' | 'progress_status' | 'resume_content_updated'
  document_id?: number
  filename?: string
}

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  created_at: string
  msg_metadata?: MessageMetadata | null
}

export interface Chat {
  id: string
  title: string
  userId?: string
  user_id?: string // API returns snake_case
  messages: Message[]
  createdAt?: string
  updatedAt?: string
  created_at?: string // API returns snake_case
  updated_at?: string // API returns snake_case
}

export interface ApiError {
  message: string
  statusCode: number
  errors?: Record<string, string[]>
}

// Document types
export type DocumentStatus = 'uploading' | 'processing' | 'ready' | 'failed' | 'invalid_document'
export type RevisionStatus = 'generating' | 'compiling' | 'ready' | 'failed'
export type DocumentType = 'resume' | 'cover_letter'
export type JobMatchingStatus = 'pending' | 'matching' | 'completed' | 'failed'

export interface DocumentUploadUrlRequest {
  filename: string
  content_type: string
  file_size: number
}

export interface DocumentUploadUrlResponse {
  document_id: number
  upload_url: string
  expires_in: number
}

export interface DocumentRevision {
  id: number
  document_id: number
  revision_number: number
  prompt_summary?: string
  status: RevisionStatus
  created_at: string
}

export interface Document {
  id: number
  user_id: number
  document_type: DocumentType
  original_filename: string
  file_size_bytes: number
  mime_type: string
  status: DocumentStatus
  job_matching_status?: JobMatchingStatus | null
  job_matching_started_at?: string | null
  job_matching_total?: number | null
  job_matching_completed_count?: number | null
  quality_score?: QualityScore | null
  created_at: string
  updated_at: string
}

export interface QualityScore {
  overall: number
  skills_coverage: number
  experience_clarity: number
  formatting: number
}

export interface DocumentDetail extends Document {
  extracted_text_preview?: string
  revisions: DocumentRevision[]
  parsed_resume_data?: ResumeContent | null
  draft_content_json?: ResumeContent | null
  quality_score?: QualityScore | null
}

export interface RevisionDownloadResponse {
  download_url: string
  filename: string
  expires_in: number
  format: 'pdf' | 'docx'
}

export interface RevisionGenerateRequest {
  job_posting_id?: number
  focus_areas?: string[]
  trigger?: 'button' | 'ai_intent' | 'api'
  template_id?: TemplateId
  use_draft?: boolean
}

export interface RevisionReadyEvent {
  document_id: number
  revision_id: number
  revision_number: number
  status: RevisionStatus // 'compiling' or 'ready' (may need polling if compiling)
}

export interface AddLearnedSkillsRequest {
  skills: Array<{
    name: string
    level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  }>
}

export interface AddLearnedSkillsResponse {
  added_count: number
  skills_added: string[]
}

// Job Posting types
export type JobPostingStatus = 'pending' | 'parsing' | 'ready' | 'failed'

export interface JobPostingCreate {
  text?: string
  url?: string
  source_url?: string
}

export interface JobPostingBase {
  id: number
  title?: string
  company?: string
  status: JobPostingStatus
  created_at: string
}

export interface JobPosting extends JobPostingBase {
  raw_text: string
  source_url?: string
  parsed_data?: Record<string, unknown>
  error_message?: string
}

export interface JobPostingList {
  items: JobPostingBase[]
  total: number
}

// Resume Match types
export interface MatchScoreBreakdown {
  skills?: number
  experience?: number
  education?: number
}

export interface MatchRecommendation {
  priority: number
  action: string
  impact: string
  reason?: string
}

export interface RoleAlignmentItem {
  dimension: string
  match: string // Strong/Moderate/Low/None
  notes: string
}

export interface ResumeMatchRequest {
  job_text?: string
  job_url?: string
  job_posting_id?: number // legacy
}

export interface ResumeMatchResponse {
  match_id?: number
  overall_score: number
  breakdown: MatchScoreBreakdown
  matched_skills: string[]
  missing_skills: string[]
  role_alignment?: RoleAlignmentItem[]
  recommendations: string[]
  summary?: string
  better_fit_roles?: string[]
  explanation?: string
}

// Legacy response format (unused)
export interface _LegacyResumeMatchResponse {
  match_id: number
  overall_score: number
  breakdown: MatchScoreBreakdown
  details?: {
    skills?: {
      matched: string[]
      missing: string[]
      partial: Array<{ required: string; found: string }>
    }
    experience?: {
      required_years?: number
      your_years?: number
      meets_requirement?: boolean
    }
  }
  recommendations: MatchRecommendation[]
  summary?: string
  created_at: string
}

export interface ResumeMatchListItem {
  id: number
  job_posting: JobPostingBase
  overall_score: number
  created_at: string
}

export interface ResumeMatchList {
  matches: ResumeMatchListItem[]
}

// Resume Content types (structured JSON for preview and PDF generation)
export type TemplateId =
  | 'modern'
  | 'pikachu'
  | 'executive'
  | 'two_column_photo'
  | 'bold_creative'
  | 'academic_cv'
  | 'timeline'

export interface ResumeContact {
  name: string
  email?: string
  phone?: string
  location?: string
  linkedin?: string
  website?: string
}

export interface ResumeExperience {
  company: string
  title: string
  start_date: string
  end_date?: string
  location?: string
  achievements: string[]
}

export interface ResumeEducation {
  institution: string
  degree: string
  field_of_study?: string
  /** Start of the study period. Absent on education saved before PEL-1018. */
  start_date?: string
  /** End of the study period; "Present" while the person is still studying. */
  graduation_date?: string
  gpa?: string
  honors?: string[]
}

export interface ResumeProject {
  name: string
  description?: string
  technologies?: string[]
  url?: string
}

export interface ResumeContent {
  contact: ResumeContact
  summary?: string
  experience: ResumeExperience[]
  education: ResumeEducation[]
  skills: string[]
  certifications?: string[]
  projects?: ResumeProject[]
  languages?: string[]
  // Canonical job title chosen by the backend parser from the picker's role
  // vocabulary. Preferred over lexical matching when present.
  suggested_canonical_role?: string | null
}

// SSE event for resume content update from chat
export interface ResumeContentUpdatedEvent {
  document_id: number
  content: ResumeContent
}

// Resume Builder (per-draft canvas). How a draft is seeded.
export type ResumeBuilderSeedType = 'paste_text' | 'upload' | 'jd' | 'scratch' | 'profile'

// Templates the builder can render and persist. Alias to TemplateId so the
// Export panel's template picker (all 8 templates) and the builder's own
// persisted template field can't drift out of sync.
export type BuilderTemplate = TemplateId

export interface ResumeBuilderCreateRequest {
  seed_type: ResumeBuilderSeedType
  raw_input?: string
  job_posting_id?: number
}

export interface ResumeBuilderChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ResumeBuilderDraft {
  id: number
  seed_type?: ResumeBuilderSeedType | null
  title: string
  template: BuilderTemplate
  content: ResumeContent
  // Raw seed text (pasted resume / pasted JD / extracted upload text), shown in
  // the canvas as an expandable "here's what I gave you" chat bubble.
  seed_text?: string | null
  // Persisted conversation, so the canvas can restore chat on open/reload.
  messages?: ResumeBuilderChatMessage[]
  // Field paths the user manually edited (provenance) — the canvas re-hydrates
  // its no-clobber set from these so edits stay marked across reload.
  locked_fields?: string[]
  job_posting_id?: number | null
  quality_score?: QualityScore | null
  quality_score_is_current?: boolean
  updated_at?: string | null
}

export interface ResumeBuilderScoreResponse {
  quality_score: QualityScore
  content_hash: string
  scorer_version: string
  cached: boolean
}

export interface ResumeBuilderDraftListItem {
  id: number
  title: string
  // Content + template so the drafts view can render a live thumbnail preview
  // of each draft (client-side render) instead of a generic document icon.
  template: BuilderTemplate
  content: ResumeContent
  updated_at?: string | null
}

// Export panel customization: live-preview-only style overrides. Every field
// optional so an empty object renders identically to the plain download.
export interface ResumeExportOptions {
  template?: BuilderTemplate
  accent_color?: string
  name_size?: number
  section_header_size?: number
  sub_header_size?: number
  body_size?: number
}

// A chat refine turn's result: the updated draft plus the assistant reply.
export interface ResumeBuilderChatResponse {
  draft: ResumeBuilderDraft
  message: string
}

// External Job Listing types
export type ExternalJobSource = 'jsearch' | 'techmap' | 'adzuna' | 'the_muse' | 'remotive'
export type JobType = 'full_time' | 'part_time' | 'contract' | 'internship' | 'temporary'

// Phase 2 enrichment (see backend JobEnrichmentSummary). Tone drives chip color.
export type JobBadgeTone = 'positive' | 'neutral' | 'caution'
export interface JobBadge {
  label: string
  tone: JobBadgeTone
}
export interface JobCoreSkill {
  name: string
  type?: 'hard' | 'soft' | null
  score?: number | null
}

export interface ExternalJobListingBrief {
  id: number
  title: string
  // Readable form of a composite feed title ("IN_Manager_..._Bangalore"); null
  // when `title` is already readable. Render via jobDisplayTitle().
  display_title?: string | null
  company?: string
  company_logo_url?: string
  location?: string
  is_remote: boolean
  description_snippet?: string
  salary_min?: number
  salary_max?: number
  salary_currency?: string
  job_type?: JobType
  posted_at?: string
  created_at: string
  source: ExternalJobSource
  source_url: string
  // Enrichment summary (Phase 2): light signals carried on every card/match.
  work_model?: string | null // remote | hybrid | onsite
  seniority?: number | null // 0=intern .. 5=exec
  min_yoe?: number | null
  max_yoe?: number | null
  h1b_sponsor?: boolean | null
  work_auth_required?: boolean | null
  citizen_only?: boolean | null
  clearance_required?: boolean | null
  country_code?: string | null // ISO 3166-1 alpha-2
  region_code?: string | null // ISO 3166-2
  language?: string | null // ISO 639-1
  badges?: JobBadge[]
}

export interface ExternalJobListing extends ExternalJobListingBrief {
  description: string
  salary_period?: string
  category?: string
  employer_type?: string
  job_benefits?: string[]
  job_required_skills?: string[]
  job_highlights?: Record<string, unknown>
  expires_at?: string
  created_at: string
  updated_at: string
  // Heavy enrichment for the JD detail view (Phase 2.2b).
  core_skills?: JobCoreSkill[] | null
  qualifications?: Record<string, unknown> | null
  taxonomy?: Record<string, unknown> | null
  languages_required?: string[] | null
}

// --- Job corpus analytics (admin console /job-corpus) ---
export interface FacetBucket {
  key: string
  label: string
  count: number
  company_count?: number | null
}
export interface CoverageStat {
  count: number
  pct: number
}
export interface CompanyCount {
  company?: string | null
  count: number
}
export interface JobCorpusSummary {
  total: number
  active: number
  superseded: number
  distinct_companies: number
  distinct_countries: number
  distinct_sources: number
  remote_count: number
  remote_pct: number
  salary_present_count: number
  salary_present_pct: number
  coverage: Record<'text' | 'llm' | 'embedded', CoverageStat>
  // Field fill-rate within the LLM-enriched subset (denominator = llm_base).
  llm_base: number
  llm_extraction: Record<string, CoverageStat>
  // Interim active/inactive via expires_at (Phase 5.9 live-link supersedes).
  status: { active: number; expired: number; no_expiry: number }
  freshness: { d30: number; d90: number; d180: number; stale: number; undated: number }
  recency: FacetBucket[]
  top_companies: CompanyCount[]
  top10_share: number
  top25_share: number
}
export interface JobCorpusExploreResponse {
  total: number
  facets: Record<string, FacetBucket[]>
  items: ExternalJobListingBrief[]
  skip: number
  limit: number
}
export interface CompanyRow {
  company: string
  count: number
  sources: string[]
  country_count: number
  llm_count: number
  logo_url?: string | null
}
export interface JobCorpusCompaniesResponse {
  total_companies: number
  total_jobs: number
  companies: CompanyRow[]
  available_sources: string[]
  skip: number
  limit: number
}
export interface JobCorpusCompaniesParams {
  q?: string
  source?: string
  sort?: 'company' | 'ats_count' | 'count' | 'country_count' | 'llm_count'
  order?: 'asc' | 'desc'
  skip?: number
  limit?: number
}
export interface JobCorpusFilters {
  q?: string
  source?: string
  function?: string
  industry?: string
  seniority?: number
  work_model?: string
  country_code?: string
  company?: string
  job_type?: string
  language?: string
  is_remote?: boolean
  salary_min?: number
  enrichment?: string
  freshness?: string
  status?: string
  sort?: string
  skip?: number
  limit?: number
}

export interface ExternalJobListingList {
  items: ExternalJobListingBrief[]
  total: number
  skip: number
  limit: number
}

export interface ExternalJobSearchParams {
  query?: string
  location?: string
  country?: string
  is_remote?: boolean
  job_type?: JobType
  salary_min?: number
  category?: string
  skip?: number
  limit?: number
}

/**
 * Letter grade derived server-side from `global_score` band.
 * Bands match career-ops' 1-5 interpretation:
 *   A: 4.5+   B: 4.0+   C: 3.5+   D: 3.0+   F: <3.0
 */
export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F'

export type LegitimacyStatus = 'high_confidence' | 'proceed_with_caution' | 'suspicious'

export type DimensionKey = 'cv_match' | 'north_star' | 'comp' | 'cultural' | 'red_flag'

export interface RedFlag {
  type: string
  severity: 'low' | 'medium' | 'high'
  note: string
}

/**
 * Per-dimension scores from the multi-dim rubric (santifer/career-ops, MIT).
 * All nullable on legacy match rows that pre-date the rubric — frontend
 * falls back to the legacy CircularProgress + overall_score in that case.
 */
export interface MatchDimensions {
  cv_match_score?: number | null
  north_star_score?: number | null
  comp_score?: number | null
  cultural_score?: number | null
  red_flag_score?: number | null
  global_score?: number | null
  letter_grade?: LetterGrade | null
  legitimacy?: LegitimacyStatus | null
  red_flags?: RedFlag[] | null
  dimension_rationale?: Record<string, string> | null
  match_confidence?: 'full' | 'partial' | 'none' | null
}

export interface ExternalJobMatch extends MatchDimensions {
  id: number
  document_id: number
  /** Filename of the resume this match was analyzed against (PEL-413). */
  resume_filename?: string | null
  job_listing_id: number
  overall_score: number
  skills_score?: number
  experience_score?: number
  matched_skills: string[]
  missing_skills: string[]
  match_summary?: string
  created_at: string
  updated_at: string
}

export interface ExternalJobMatchWithJob extends ExternalJobMatch {
  job_listing: ExternalJobListingBrief
}

export interface ExternalJobMatchRequest {
  document_id?: number
}

export interface ExternalJobMatchResponse extends MatchDimensions {
  match_id: number
  job_listing: ExternalJobListingBrief
  overall_score: number
  skills_score?: number
  experience_score?: number
  matched_skills: string[]
  missing_skills: string[]
  match_summary?: string
  created_at: string
}

export interface RecommendedJobsResponse {
  items: ExternalJobMatchWithJob[]
  total: number
}

export interface JobCategoriesResponse {
  categories: string[]
}

export interface JobApplyResumeStatus {
  apply_run_id: number
  job_listing_id: number
  status: string
  base_document_id?: number
  revision_id?: number
  previous_global_score?: number
  new_global_score?: number
  previous_score?: number
  new_score?: number
  delta_percent?: number
  reanalysis_matched_skills?: string[] | null
  reanalysis_missing_skills?: string[] | null
  reanalysis_match_summary?: string | null
  error_code?: string
  error_message?: string
  updated_at: string
}

export type CoverLetterRunStatus =
  | 'idle'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'failed_no_resume'

export interface CoverLetterStatus {
  cover_letter_run_id: number
  job_listing_id: number
  status: CoverLetterRunStatus
  base_document_id?: number | null
  tracker_entry_id?: number | null
  tone?: string | null
  cover_letter_text?: string | null
  error_code?: string | null
  error_message?: string | null
  updated_at: string
}

export interface CoverLetterStartRequest {
  tone?: string
  document_id?: number
  force_regenerate?: boolean
  source?: string
}

export type JobSearchStatus = 'pending' | 'matching' | 'completed' | 'failed'
export type JobSearchSourceType = 'resume_scan' | 'custom_search'

export interface JobSearch {
  id: number
  user_id: number
  document_id: number
  chat_id?: number
  job_title?: string
  location?: string
  job_type?: JobType
  is_remote?: boolean
  source_type: JobSearchSourceType
  source_label?: string
  criteria_snapshot?: {
    search_criteria?: Record<string, unknown>
    resume_criteria?: Record<string, unknown>
  }
  status: JobSearchStatus
  started_at?: string
  completed_at?: string
  total_jobs?: number
  completed_jobs?: number
  match_count?: number
  created_at: string
  updated_at: string
}

export interface JobSearchCreateRequest {
  document_id?: number
  job_title?: string
  location?: string
  job_type?: JobType
  is_remote?: boolean
  chat_id?: number
  job_ids?: number[]
}

export interface JobSearchListResponse {
  items: JobSearch[]
  total: number
}

export interface JobSearchWithMatches extends JobSearch {
  matches: ExternalJobMatchWithJob[]
}

export * from './interview'
export * from './learning'
export * from './tracker'
export * from './resource'
